import React, { useEffect, useState } from 'react';
import { Alert } from '../../../utils/Alert';
import MainLayout from '../../../components/layout/MainLayout';
import AdminSidebar from '../AdminSidebar';
import axios from 'axios';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaTimes, FaBox } from 'react-icons/fa';
import './PackagePage.css';
import '../Admin.css';

import { API_URL } from '../../../config';
interface Creator {
  email: string;
  username: string;
  role: string;
  id: number;
  created_at: string;
  updated_at: string | null;
}

interface Package {
  id: number;
  name: string;
  description: string;
  created_by: number;
  created_at: string;
  updated_at: string | null;
  creator: Creator;
}

interface PackageFormData {
  name: string;
  description: string;
}

const PackagePage: React.FC<{ user: any; onLogout: () => void }> = ({ user, onLogout }) => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [formData, setFormData] = useState<PackageFormData>({
    name: '',
    description: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Add delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    packageId: number | null;
    packageName: string;
  }>({
    show: false,
    packageId: null,
    packageName: ''
  });

  // Add notification state
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
    show: boolean;
  } | null>(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        Alert.error('Oops...', 'Authentication token not found');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API_URL}/api/packages/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
        },
        params: {
          skip: 0,
          limit: 100
        }
      });
      const normalized = (response.data || []).map((pkg: Package) => ({
        ...pkg,
        description: pkg.description || '',
      }));
      setPackages(normalized);
    } catch (err) {
      console.error('Error fetching packages:', err);
      Alert.error('Oops...', 'Failed to fetch packages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePackage = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Prepare the data for API submission
      const packageData = {
        name: formData.name,
        description: formData.description,
      };
      
        await axios.post(`${API_URL}/api/packages/`, packageData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      setShowCreateModal(false);
      setFormData({ name: '', description: '' });
      fetchPackages();
      Alert.success('Success', 'Package created successfully!');
    } catch (err) {
      console.error('Error creating package:', err);
      Alert.error('Oops...', 'Failed to create package');
      Alert.error('Error', 'Failed to create package');
    }
  };

  const handleEditPackage = async () => {
    if (!selectedPackage) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Prepare the data for API submission
      const packageData = {
        name: formData.name,
        description: formData.description,
      };
      
      await axios.put(`${API_URL}/api/packages/${selectedPackage.id}`, packageData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      setShowEditModal(false);
      setSelectedPackage(null);
      setFormData({ name: '', description: '' });
      fetchPackages();
      Alert.success('Success', 'Package updated successfully!');
    } catch (err) {
      console.error('Error updating package:', err);
      Alert.error('Oops...', 'Failed to update package');
      Alert.error('Error', 'Failed to update package');
    }
  };

  const handleDeleteClick = (pkg: Package) => {
    setDeleteConfirmation({
      show: true,
      packageId: pkg.id,
      packageName: pkg.name
    });
  };

  const closeDeleteModal = () => {
    setDeleteConfirmation({ show: false, packageId: null, packageName: '' });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.packageId) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      await axios.delete(`${API_URL}/api/packages/${deleteConfirmation.packageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
        }
      });
      fetchPackages();
      closeDeleteModal();
      Alert.success('Success', 'Package deleted successfully!');
    } catch (err) {
      console.error('Error deleting package:', err);
      Alert.error('Oops...', 'Failed to delete package');
      Alert.error('Error', 'Failed to delete package');
    }
  };

  const fetchPackageById = async (id: number): Promise<Package | null> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const response = await axios.get(`${API_URL}/api/packages/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
        }
      });
      return {
        ...response.data,
        description: response.data.description || '',
      };
    } catch (err) {
      console.error('Error fetching package:', err);
      return null;
    }
  };

  const openEditModal = async (pkg: Package) => {
    setLoading(true);
    try {
      // Fetch the latest package data
      const latestPackage = await fetchPackageById(pkg.id);
      if (latestPackage) {
        setSelectedPackage(latestPackage);
        setFormData({
          name: latestPackage.name,
          description: latestPackage.description || '',
        });
        setShowEditModal(true);
      } else {
        // Fallback to existing data if fetch fails
        setSelectedPackage(pkg);
        setFormData({
          name: pkg.name,
          description: pkg.description || '',
        });
        setShowEditModal(true);
        Alert.error('Error', 'Failed to fetch latest package data');
      }
    } catch (err) {
      console.error('Error opening edit modal:', err);
      // Fallback to existing data
      setSelectedPackage(pkg);
      setFormData({
        name: pkg.name,
        description: pkg.description || '',
      });
      setShowEditModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Add search handler
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Show notification helper function
  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    if (type === 'success') {
      Alert.success('Success!', message);
    } else if (type === 'error') {
      Alert.error('Oops...', message);
    } else {
      Alert.warning('Notice', message);
    }
  };

  // Filter packages based on search term
  const filteredPackages = React.useMemo(() => {
    return packages.filter(pkg => 
      (pkg.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (pkg.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [packages, searchTerm]);

  return (
    <MainLayout user={user} onLogout={onLogout} sidebarContent={<AdminSidebar />}>
      <div className="package-page-root font-kumbh bg-[#FAFBFF] min-h-screen">
        {/* Header Section */}
        {/* Header Section */}
        <div className="px-8 py-6 flex items-center justify-between w-full">
          <div className="flex items-center " style={{ gap: 0 }}>
            <h1 className="admin-title">Manage Packages</h1>
          </div>
          <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-[#111827] hover:bg-gray-800 text-white font-kumbh text-base font-semibold shadow-lg transition active:scale-95 min-w-[160px]"
              style={{ fontFamily: 'Inter, sans-serif', boxShadow: '0 8px 24px #0050FF22' }}
            >
              Add Package
            </button>
              </div>

        {/* Filters & Actions Bar */}
        <div className="w-full flex justify-center mt-4 ">
          <div className="bg-white rounded-lg flex flex-wrap md:flex-nowrap items-center justify-between p-6 w-full max-w-7xl gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="relative min-w-[240px]">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {FaSearch({})}
                </span>
                <input
                  type="text"
                  placeholder="Search packages.."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 shadow focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white text-sm w-full font-kumbh placeholder-gray-400"
                  style={{ fontFamily: 'Inter, sans-serif', boxShadow: '0 2px 6px #0001' }}
                />
              </div>
            </div>
            
          </div>
            </div>

          
        {/* Main Content */}
        <div className="flex-grow overflow-auto p-8 pt-2">
          <div className="max-w-7xl mx-auto">
          {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700 font-kumbh">{error}</p>
                  </div>
                </div>
            </div>
          )}
          
            {/* Loading State */}
            {loading && (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
          
                         {/* Packages Grid */}
             {!loading && (
             <div className="packages-grid">
                {filteredPackages.map(pkg => (
                <div key={pkg.id} className="package-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 rounded-lg bg-blue-50">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#111827]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                      </svg>
                    </div>
                    <div className="package-card-actions">
                      <button
                        onClick={() => openEditModal(pkg)}
                        className="package-card-action-btn"
                        title="Edit package"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteClick(pkg)}
                        className="package-card-action-btn delete"
                        title="Delete package"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <h3 className="package-card-title">{pkg.name}</h3>
                  <p className="package-card-desc">{pkg.description || '-'}</p>
                  <div className="package-card-uploaded">
                    <p>Created: {new Date(pkg.created_at).toLocaleDateString()}</p>
                    <p className="text-xs text-gray-600">By: {pkg.creator?.username}</p>
                  </div>
                </div>
              ))}
                {filteredPackages.length === 0 && !loading && (
                  <div className="col-span-2 bg-white rounded-lg shadow-sm p-8 text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    <h2 className="text-xl font-medium text-gray-600 mb-2">No packages available</h2>
                    <p className="text-gray-600 mb-6">Create your first package by clicking the button above.</p>
                  </div>
                )}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Create Package Modal */}
          {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col">
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Create New Package</h2>
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({ name: '', description: '' });
                }}
                  className="text-gray-400 hover:text-gray-600"
              >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

              <div className="space-y-3">
                  <div>
                  <label className="block text-sm font-medium text-gray-600 ">Package Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter package name"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-700 focus:border-blue-500"
                    />
                  </div>

                  <div>
                  <label className="block text-sm font-medium text-gray-600 ">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter package description"
                    rows={2}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-700 focus:border-blue-500"
                    />
                  </div>

              </div>

              <div className="mt-4 flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowCreateModal(false);
                        setFormData({ name: '', description: '' });
                      }}
                  className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreatePackage}
                      disabled={!formData.name.trim()}
                  className={`px-4 py-1.5 text-sm font-medium rounded-lg ${
                        !formData.name.trim()
                    ? 'bg-blue-300 text-white cursor-not-allowed' 
                    : 'bg-[#111827] text-white hover:bg-gray-800'
                      }`}
                    >
                  Create Package
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

      {/* Edit Package Modal */}
          {showEditModal && selectedPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col">
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Edit Package</h2>
              <button 
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedPackage(null);
                }}
                  className="text-gray-400 hover:text-gray-600"
              >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

              <div className="space-y-3">
                  <div>
                  <label className="block text-sm font-medium text-gray-600 ">Package Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter package name"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-700 focus:border-blue-500"
                    />
                  </div>

                  <div>
                  <label className="block text-sm font-medium text-gray-600 ">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter package description"
                    rows={2}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-700 focus:border-blue-500"
                    />
                  </div>

              </div>

              <div className="mt-4 flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowEditModal(false);
                    setSelectedPackage(null);
                      }}
                  className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEditPackage}
                      disabled={!formData.name.trim()}
                  className={`px-4 py-1.5 text-sm font-medium rounded-lg ${
                        !formData.name.trim()
                    ? 'bg-blue-300 text-white cursor-not-allowed' 
                    : 'bg-[#111827] text-white hover:bg-gray-800'
                      }`}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Delete Package
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete <strong>{deleteConfirmation.packageName}</strong>? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={loading}
                className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition active:scale-95"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={loading}
                className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 sm:mt-0 sm:w-auto sm:text-sm transition active:scale-95"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Popup */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <div className={`rounded-lg shadow-lg p-4 border-l-4 ${
            notification.type === 'success' 
              ? 'bg-green-50 border-green-400 text-green-800' 
              : notification.type === 'error'
              ? 'bg-red-50 border-red-400 text-red-800'
              : 'bg-blue-50 border-blue-400 text-blue-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-3 ${
                  notification.type === 'success' ? 'bg-green-400' 
                  : notification.type === 'error' ? 'bg-red-400' 
                  : 'bg-blue-400'
                }`}></div>
                <p className="text-sm font-medium">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default PackagePage; 

